import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Divider,
  CircularProgress, Alert, MenuItem, TextField, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { jsPDF } from 'jspdf';
import { useNavigate, useParams } from 'react-router-dom';
import { normalizeBranch, loadLogos, renderStandardHeader, renderStandardFooter } from '../services/pdfCommon';
import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const STATUS_CONFIG = {
  pending_upload: { label: 'Pendiente',   color: '#D97706', bg: '#FEF3C7' },
  processing:     { label: 'Procesando',  color: '#2563EB', bg: '#DBEAFE' },
  pending_review: { label: 'En revisión', color: '#CA8A04', bg: '#FEF9C3' },
  approved:       { label: 'Aprobado',    color: '#16A34A', bg: '#DCFCE7' },
  reported:       { label: 'Informado',   color: '#059669', bg: '#D1FAE5' },
};

const PRIORITY_CHIP = {
  urgent:  { label: 'Urgente', color: '#DC2626', bg: '#FEE2E2' },
  routine: { label: 'Rutina',  color: '#6B7280', bg: '#F3F4F6' },
};

const C = { primary: [26, 58, 92], teal: [44, 184, 160], text: [30, 30, 30], muted: [100, 110, 120], light: [244, 246, 248], border: [220, 225, 230], white: [255, 255, 255] };

export const printOrderPDF = async (order, calcAgeFn, branchData = {}) => {
  const branch = normalizeBranch(branchData);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210;
  const margin = 18;
  const cw = pw - margin * 2;

  const logos = await loadLogos();
  const dateStr = order.createdAt?.toDate
    ? order.createdAt.toDate().toLocaleDateString('es-CL')
    : new Date().toLocaleDateString('es-CL');
  renderStandardHeader(doc, branch, logos, {
    title: 'ORDEN DE LABORATORIO',
    number: order.orderNumber || '—',
    date: dateStr,
  });

  let y = 40;
  const sf = (arr) => doc.setFillColor(...arr);
  const sc = (arr) => doc.setTextColor(...arr);
  const sd = (arr) => doc.setDrawColor(...arr);

  // ── PATIENT / VET BLOCK ──
  sf(C.light); sd(C.border);
  doc.roundedRect(margin, y, cw, 46, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.primary);
  doc.text('DATOS DEL PACIENTE', margin + 4, y + 6);
  sd(C.teal); doc.setLineWidth(0.4);
  doc.line(margin + 4, y + 8, margin + cw / 2 - 4, y + 8);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
  const col1 = margin + 4;
  const col2 = margin + cw / 2 + 4;
  const lh = 6;
  let ly = y + 14;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Nombre:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientName || '—', col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Especie:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientSpecies || '—', col2 + 20, ly);
  ly += lh;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Raza:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientBreed || '—', col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Sexo:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientSex || '—', col2 + 20, ly);
  ly += lh;
  const age = calcAgeFn(order.patientBirthdate);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Edad:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(age || '—', col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Propietario:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.ownerName || '—', col2 + 28, ly);
  ly += lh;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Médico V.:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.vetName || '—', col1 + 24, ly);

  y += 52;

  // ── EXAMS TABLE ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.primary);
  doc.text('EXÁMENES SOLICITADOS', margin, y);
  sd(C.teal); doc.setLineWidth(0.5);
  doc.line(margin, y + 2, margin + 60, y + 2);
  y += 7;

  sf(C.primary); sd(C.primary);
  doc.rect(margin, y, cw, 7, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); sc(C.white);
  doc.text('#', margin + 3, y + 4.8);
  doc.text('Nombre del examen', margin + 12, y + 4.8);
  y += 7;

  const exams = order.examsRequested || [];
  exams.forEach((e, i) => {
    sf(i % 2 === 0 ? C.white : C.light); sd(C.border);
    doc.rect(margin, y, cw, 7, 'FD');
    doc.setFont('helvetica', 'normal'); sc(C.muted);
    doc.text(String(i + 1), margin + 3, y + 4.8);
    sc(C.text); doc.setFont('helvetica', 'bold');
    doc.text(e.examTypeName || '—', margin + 12, y + 4.8);
    y += 7;
  });

  y += 8;

  // ── NOTES ──
  if (order.notes) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.primary);
    doc.text('NOTAS CLÍNICAS RELEVANTES', margin, y);
    sd(C.teal); doc.setLineWidth(0.5); doc.line(margin, y + 2, margin + 46, y + 2);
    y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
    const lines = doc.splitTextToSize(order.notes, cw);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 6;
  }

  // ── SIGNATURE BLOCK ──
  if (y > 230) { doc.addPage(); y = margin; }
  y = Math.max(y, 210);
  sd(C.border); doc.setLineWidth(0.3);
  doc.line(margin, y, margin + 60, y);
  doc.line(margin + 90, y, margin + 150, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); sc(C.muted);
  doc.text('Firma Médico Veterinario', margin + 2, y + 5);
  doc.text('Firma Propietario / Tutor', margin + 92, y + 5);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); sc(C.text);
  const vetNameLines = doc.splitTextToSize(order.vetName || '', 58);
  doc.text(vetNameLines, margin + 2, y + 12);
  const ownerNameLines = doc.splitTextToSize(order.ownerName || '', 58);
  doc.text(ownerNameLines, margin + 92, y + 12);

  renderStandardFooter(doc, branch);

  doc.save(`Orden_${order.orderNumber || order.id}.pdf`);
};

export const calcAge = (birthdate) => {
  if (!birthdate) return null;
  const birth = new Date(birthdate);
  if (isNaN(birth)) return null;
  const now = new Date();
  const years = now.getFullYear() - birth.getFullYear();
  const months = now.getMonth() - birth.getMonth() + years * 12;
  if (months < 1) return 'Recién nacido';
  if (months < 12) return `${months} mes${months > 1 ? 'es' : ''}`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m > 0 ? `${y} año${y > 1 ? 's' : ''} ${m} mes${m > 1 ? 'es' : ''}` : `${y} año${y > 1 ? 's' : ''}`;
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.8, borderBottom: '1px solid #F1F5F9' }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={600} color={LAB_PRIMARY}>{value || '—'}</Typography>
  </Box>
);

export default function LabOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { activeBranch } = useBranch();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  const loadOrder = async () => {
    try {
      const snap = await getDoc(doc(db, 'labOrders', id));
      if (!snap.exists()) { setError('Orden no encontrada.'); setLoading(false); return; }
      const orderData = { id: snap.id, ...snap.data() };

      // Enrich with live patient data when stored fields are missing
      if (orderData.patientId && (!orderData.patientSpecies || !orderData.patientSex)) {
        try {
          const pSnap = await getDoc(doc(db, 'patients', orderData.patientId));
          if (pSnap.exists()) {
            const p = pSnap.data();
            if (!orderData.patientSpecies) orderData.patientSpecies = p.species || '';
            if (!orderData.patientSex)     orderData.patientSex     = p.genero  || '';
            if (!orderData.patientBirthdate) orderData.patientBirthdate = p.birthDate || '';
            if (!orderData.patientBreed)   orderData.patientBreed   = p.breed   || '';
          }
        } catch (_) { /* non-critical, continue */ }
      }

      setOrder(orderData);
    } catch (e) {
      setError('Error al cargar la orden: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [id]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    setSavingStatus(true);
    try {
      await updateDoc(doc(db, 'labOrders', id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'labAuditLog'), {
        orderId: id,
        action: 'status_changed',
        performedBy: currentUser?.uid || '',
        performedAt: serverTimestamp(),
        details: `Estado cambiado a "${STATUS_CONFIG[newStatus]?.label}". ${statusNote ? 'Nota: ' + statusNote : ''}`,
      });
      setSnackbar({ open: true, message: 'Estado actualizado correctamente.', severity: 'success' });
      setStatusDialog(false);
      setStatusNote('');
      loadOrder();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al actualizar estado: ' + e.message, severity: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')} sx={{ mt: 2 }}>
        Volver
      </Button>
    </Box>
  );

  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending_upload;
  const priCfg = PRIORITY_CHIP[order.priority] || PRIORITY_CHIP.routine;
  const canChangeStatus = ['admin', 'user', 'gestion'].includes(userData?.role);

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab/orders')}
            sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>
            Volver
          </Button>
          <ScienceIcon sx={{ color: LAB_PRIMARY }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>
              Orden {order.orderNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Creada el {formatDate(order.createdAt)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={priCfg.label} sx={{ bgcolor: priCfg.bg, color: priCfg.color, fontWeight: 700 }} />
          <Chip label={cfg.label} sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700 }} />
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
            onClick={async () => { await printOrderPDF(order, calcAge, activeBranch); }}
            sx={{ borderColor: LAB_TEAL, color: LAB_TEAL, fontWeight: 600 }}>
            Imprimir PDF
          </Button>
          {canChangeStatus && (
            <Button size="small" variant="outlined" startIcon={<EditIcon />}
              onClick={() => { setNewStatus(order.status); setStatusDialog(true); }}
              sx={{ borderColor: LAB_PRIMARY, color: LAB_PRIMARY, fontWeight: 600 }}>
              Cambiar estado
            </Button>
          )}
          {(order.status === 'pending_upload' || order.status === 'processing') && (
            <Button size="small" variant="contained" startIcon={<UploadFileIcon />}
              onClick={() => navigate(`/lab/upload?orderId=${order.id}`)}
              sx={{ bgcolor: LAB_TEAL, fontWeight: 600 }}>
              Subir resultado
            </Button>
          )}
          {(order.status === 'pending_review' || order.status === 'approved' || order.status === 'reported') && order.resultId && (
            <Button size="small" variant="contained" startIcon={<ArticleIcon />}
              onClick={() => navigate(`/lab/review/${order.resultId}`)}
              sx={{ bgcolor: LAB_PRIMARY, fontWeight: 600 }}>
              Ver informe
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Paciente */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PetsIcon sx={{ color: LAB_TEAL }} />
              <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>Paciente</Typography>
            </Box>
            <InfoRow label="Nombre" value={order.patientName} />
            <InfoRow label="Especie" value={order.patientSpecies} />
            <InfoRow label="Raza" value={order.patientBreed} />
            <InfoRow label="Sexo" value={order.patientSex} />
            <InfoRow label="Edad" value={calcAge(order.patientBirthdate)} />
            <InfoRow label="Propietario" value={order.ownerName} />
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <PersonIcon sx={{ color: LAB_TEAL, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY}>Médico Solicitante</Typography>
            </Box>
            <InfoRow label="Profesional" value={order.vetName} />
          </Paper>
        </Grid>

        {/* Exámenes */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              Exámenes Solicitados ({(order.examsRequested || []).length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Examen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.examsRequested || []).map((e, i) => (
                    <TableRow key={i} sx={{ '&:hover': { bgcolor: '#F0F9FF' } }}>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', width: 40 }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: LAB_PRIMARY }}>{e.examTypeName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {order.notes && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
              <Typography variant="subtitle2" fontWeight={700} color={LAB_PRIMARY} mb={1}>
                Notas Clínicas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {order.notes}
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Dialog cambio de estado */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: LAB_PRIMARY, color: '#fff', fontWeight: 700 }}>
          Cambiar Estado
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select fullWidth size="small" label="Nuevo estado" value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth size="small" multiline rows={2} label="Nota (opcional)"
            value={statusNote} onChange={(e) => setStatusNote(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStatusDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleStatusChange} disabled={savingStatus || !newStatus}
            startIcon={savingStatus ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700 }}>
            {savingStatus ? 'Guardando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
